import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import {
  AccionTexto,
  Tarjeta,
  VacioCompacto,
} from '#components/mobile/inicio/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import {
  calendarioAno,
  pagaDelMes,
  proximaPorCobrar,
  sueldoDeVerdad,
} from '#components/mobile/nomina/nomina';
import type { ConfigNomina } from '#components/mobile/nomina/nomina';
import { PropuestaNomina } from '#components/mobile/nomina/PropuestaNomina';
import { mayuscula, nombrePaga } from '#components/mobile/nomina/textos';
import {
  useConfigNomina,
  useIngresosDelMes,
  useNominaDeducida,
} from '#components/mobile/nomina/useNomina';
import { color, num, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

const ALTO_BARRAS = 96;

/**
 * Pagas extra (maqueta 1B «Calendario», elegida por Vadik el 20-oct-2026):
 * la próxima paga extra con su importe, fecha y días que faltan, una barra
 * por mes con lo que se cobra (las pagas, más altas) y el «sueldo de verdad».
 * Solo informa: nada se planea antes de cobrar (Vadik, 20-oct-2026); el día
 * que llega la paga, Gavi propone repartirla. Sin nómina configurada, lo que
 * Rumbo ha deducido de los ingresos para confirmarlo con un toque, o una
 * línea que invita a contarla.
 */
export function PagasExtra({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const { config, guardar, pagador } = useConfigNomina();
  const deducida = useNominaDeducida(config == null);
  const titulo = t('Extra payments');
  const hoy = monthUtils.currentDay();
  // Lo cobrado este mes: la paga cuenta como llegada cuando llega el dinero,
  // no cuando pasa la fecha prevista.
  const ingresosMes = useIngresosDelMes(hoy.slice(0, 7), pagador);

  if (!config && deducida) {
    return (
      <Tarjeta titulo={titulo} data-testid="inicio-pagas-extra">
        <PropuestaNomina
          deducida={deducida}
          onConfirmar={guardar}
          onCorregir={nueva => {
            guardar(nueva);
            void navigate('/nomina', { state: { editar: true } });
          }}
        />
      </Tarjeta>
    );
  }

  if (!config || config.pagas.length === 0 || config.neta <= 0) {
    return (
      <Tarjeta titulo={titulo} sinTarjeta data-testid="inicio-pagas-extra">
        <VacioCompacto
          texto={
            config && config.pagas.length === 0
              ? t('You get the same every month: no extra payments.')
              : t('Do you get extra payments? Tell Rumbo and it warns you.')
          }
          accion={config ? t('Change') : t('Set up')}
          onAccion={() => void navigate('/nomina')}
        />
      </Tarjeta>
    );
  }

  const delMes = pagaDelMes(config, hoy, ingresosMes);
  const proxima = proximaPorCobrar(config, hoy, ingresosMes);
  if (!proxima) {
    return null;
  }
  const nombre = nombrePaga(t, proxima.paga, locale);
  const fecha = monthUtils.format(proxima.fecha, "d 'de' MMMM", locale);

  return (
    <Tarjeta
      titulo={titulo}
      accion={
        <AccionTexto onPress={() => void navigate('/nomina')}>
          <Trans>Calendar</Trans>
        </AccionTexto>
      }
      data-testid="inicio-pagas-extra"
    >
      <View style={{ gap: 12 }}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <View style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <Text style={{ ...texto.etiqueta, color: color.fg2 }}>
              {mayuscula(nombre)}
            </Text>
            <Text style={{ fontSize: 28, fontWeight: 800, ...num }}>
              <PrivacyFilter>
                {format(proxima.importe, 'financial')}
              </PrivacyFilter>
            </Text>
            <Text style={{ ...texto.secundario, color: color.fg2 }}>
              {proxima.dias === 0
                ? t('Arrives today')
                : t('on {{date}}', { date: fecha })}
            </Text>
          </View>
          <AnilloDias dias={proxima.dias} />
        </View>

        {tamano !== 'compacto' && (
          <Barras
            config={config}
            hoy={hoy}
            siguiente={proxima.fecha}
            extraHoy={delMes?.llego ? delMes.cobro.importe : null}
          />
        )}

        {tamano !== 'compacto' && (
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <PrivacyFilter>
              {t('Your real monthly pay: {{amount}} (the year divided by 12)', {
                amount: format(sueldoDeVerdad(config), 'financial'),
              })}
            </PrivacyFilter>
          </Text>
        )}
      </View>
    </Tarjeta>
  );
}

/** Círculo con los días que faltan (se llena según se acerca la paga). */
function AnilloDias({ dias }: { dias: number }) {
  const { t } = useTranslation();
  const r = 30;
  const largo = 2 * Math.PI * r;
  // Lleno del todo el día de cobro; vacío a seis meses o más.
  const lleno = Math.max(0, Math.min(1, 1 - dias / 183));
  return (
    <View
      style={{ width: 72, height: 72, flex: 'none', position: 'relative' }}
      aria-label={t('{{count}} days', { count: dias })}
      data-testid="pagas-dias"
    >
      <svg width={72} height={72} viewBox="0 0 72 72" aria-hidden>
        <circle
          cx={36}
          cy={36}
          r={r}
          fill="none"
          stroke={color.line}
          strokeWidth={6}
        />
        <circle
          cx={36}
          cy={36}
          r={r}
          fill="none"
          stroke={color.accent}
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={`${largo * lleno} ${largo}`}
          transform="rotate(-90 36 36)"
        />
      </svg>
      <View
        style={{
          position: 'absolute',
          inset: 0,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ fontSize: 20, fontWeight: 800, lineHeight: 1, ...num }}>
          {dias}
        </Text>
        <Text style={{ fontSize: 10, fontWeight: 600, color: color.fg3 }}>
          {t('days', { count: dias })}
        </Text>
      </View>
    </View>
  );
}

/**
 * El año en barras: una por mes, alta según lo que se cobra. Lo cobrado (hasta
 * este mes) en verde, y la paga de este mes con lo que llegó de verdad; la
 * parte de paga extra, más intensa; lo que viene, en gris, y la próxima paga
 * con borde discontinuo.
 */
function Barras({
  config,
  hoy,
  siguiente,
  extraHoy,
}: {
  config: ConfigNomina;
  hoy: string;
  siguiente: string;
  /** La paga de este mes, si ya llegó: lo que llegó de verdad. */
  extraHoy: IntegerAmount | null;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const ano = Number(hoy.slice(0, 4));
  const mesHoy = hoy.slice(0, 7);
  const meses = calendarioAno(config, ano).map(m =>
    m.mes === mesHoy && extraHoy != null
      ? { ...m, total: m.nomina + extraHoy }
      : m,
  );
  const maximo = Math.max(...meses.map(m => m.total), 1);
  const mesSiguiente = siguiente.slice(0, 7);
  const columnas = {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: 5,
  } as const;
  return (
    <View style={{ gap: 6 }}>
      <View
        role="img"
        aria-label={t(
          'Your pay for the year: higher bars are months with an extra payment',
        )}
        data-testid="pagas-barras"
        style={{ ...columnas, alignItems: 'end', height: ALTO_BARRAS }}
      >
        {meses.map(m => {
          const alto = Math.max(6, (m.total / maximo) * ALTO_BARRAS);
          const parteExtra = m.total > 0 ? (m.total - m.nomina) / m.total : 0;
          const proxima = m.mes === mesSiguiente;
          const cobrado = m.mes < mesHoy || (m.mes === mesHoy && !proxima);
          return (
            <View
              key={m.mes}
              data-mes={m.mes}
              data-proxima={proxima || undefined}
              style={{
                height: alto,
                borderRadius: 5,
                overflow: 'hidden',
                boxSizing: 'border-box',
                border: proxima ? `2px dashed ${color.accent}` : undefined,
                outline: m.mes === mesHoy ? `2px solid ${color.fg}` : undefined,
                outlineOffset: 2,
              }}
            >
              <View
                style={{
                  height: `${parteExtra * 100}%`,
                  backgroundColor: cobrado
                    ? color.accent
                    : proxima
                      ? color.surface
                      : color.line2,
                }}
              />
              <View
                style={{
                  flex: 1,
                  backgroundColor: cobrado ? color.accent : color.line,
                  opacity: cobrado ? 0.45 : 1,
                }}
              />
            </View>
          );
        })}
      </View>
      <View style={{ ...columnas }}>
        {meses.map((m, i) => {
          const conExtra = m.extras.length > 0;
          const esHoy = m.mes === mesHoy;
          return (
            <Text
              key={m.mes}
              aria-hidden
              style={{
                fontSize: 10,
                textAlign: 'center',
                fontWeight: conExtra || esHoy ? 800 : 500,
                color: conExtra ? color.accent : esHoy ? color.fg : color.fg3,
              }}
            >
              {monthUtils
                .format(
                  `2026-${String(i + 1).padStart(2, '0')}`,
                  'MMMMM',
                  locale,
                )
                .toUpperCase()}
            </Text>
          );
        })}
      </View>
    </View>
  );
}
